import axios from "axios";
import router from "../router";
import api from '@/libs/api';
import store from '@/state/store';
import { getLocalStorage } from '@/state/storage';
import { getDeviceFingerprint } from '@/libs/fingerprint';

// Constants
const TOKEN_REFRESH_THRESHOLD = 300000; // 5 minutes in ms
const MAX_RETRIES = 1; // Set maximum retry attempts
const SKIP_URLS = ['/login']; // URLs that should bypass token refresh logic

// Main axios instance with interceptors
const axiosIns = axios.create({
	baseURL: `${process.env.VUE_APP_API_BASE_URL}`,
	withCredentials: true,
	headers: {
		'Content-Type': 'application/json'
	}
})

// Secondary axios instance without interceptors for token refresh
const axiosNoInterceptors = axios.create({
	baseURL: `${process.env.VUE_APP_API_BASE_URL}`,
	withCredentials: true,
	headers: {
		'Content-Type': 'application/json'
	}
});

let isRefreshing = false;
let refreshSubscribers = [];
let currentRefreshPromise = null;

let isFetchingAuthContext = false;
let authContextSubscribers = [];
let authContextPromise = null;

let lastSecurityVerificationId = null;

// Helper functions
const shouldSkipUrl = (url) => SKIP_URLS.some(skipUrl => url.includes(skipUrl));
const handleAuthRedirect = () => {
	return store.getters['auth/loggedIn']
		? router.push({ name: 'forbidden' })
		: router.push({ name: 'login' })
};

/**
 * Adds a request to the refresh queue to be retried after token refresh
 * @param {Object} config - The axios request config
 * @returns {Promise} A promise that resolves with the updated config
 */
const addToRefreshQueue = (config) => {
	return new Promise((resolve) => {
		refreshSubscribers.push((token) => {
			config.headers.Authorization = `Bearer ${token}`;
			resolve(config); // return the updated config only
		});
	});
};

/**
 * Processes all queued requests with the new token
 * @param {string} token - The new access token
 */
const processRefreshSubscribers = (token) => {
	refreshSubscribers.forEach(callback => callback(token));
	refreshSubscribers = [];
};

/**
 * Adds a callback function to be called when the auth context is refreshed.
 * @param {Function} callback - The function to be executed once the auth context is updated.
 */
const subscribeToAuthContext = (callback) => {
	authContextSubscribers.push(callback);
};

/**
 * Executes all subscribed auth context callbacks and clears the subscriber list.
 */
const processAuthContextSubscribers = () => {
	authContextSubscribers.forEach(callback => callback());
	authContextSubscribers = [];
};

// Request Interceptor
axiosIns.interceptors.request.use(
	async (config) => {
		// Get current tenant ID from auth store or local storage
		const currentTenant = store.state.auth.currentTenant || JSON.parse(getLocalStorage('currentTenant') || null);
		const selectedCurrency = store.state.config.selectedCurrency?.code || getLocalStorage('selectedCurrency' || null);

		// Add tenant ID to headers if available
		if (currentTenant) {
			config.headers['X-Tenant-ID'] = currentTenant.id;
		}
		// Add currency to headers if available
		if (selectedCurrency) {
			config.headers['X-Currency'] = selectedCurrency;
		}

		// Add device fingerprint header
		try {
			config.headers['X-Device-Fingerprint'] = await getDeviceFingerprint();
		} catch (error) {
			console.warn('Failed to get device fingerprint:', error);
		}

		if (shouldSkipUrl(config.url)) return config;

		let token = getLocalStorage('userJwt');
		const refreshToken = getLocalStorage('userRefreshToken');

		// Skip token management if no tokens are available
		if (!token || !refreshToken) return config;

		try {
			const { exp } = JSON.parse(atob(token.split('.')[1]));
			const currentTime = Date.now();
			const timeUntilExpiry = exp * 1000 - currentTime;

			// If token is still valid with sufficient buffer, use it
			if (timeUntilExpiry > TOKEN_REFRESH_THRESHOLD) {
				config.headers.Authorization = `Bearer ${token}`;
				return config;
			}

			// If refresh is already in progress, queue the request
			if (isRefreshing) {
				return await addToRefreshQueue(config);
			}

			// Start token refresh process
			isRefreshing = true;

			currentRefreshPromise = await axiosNoInterceptors.post(
				api.API_REFRESH_TOKEN,
				{ refresh_token: refreshToken },
				{ headers: { Authorization: `Bearer ${token}` } }
			);

			const response = await currentRefreshPromise;

			const {
				access_token: newToken,
				refresh_token: newRefreshToken
			} = response.data.data;

			// Update store and local storage
			store.commit('auth/SET_TOKEN', newToken);
			store.commit('auth/SET_REFRESH_TOKEN', newRefreshToken);

			// Update request header with new token
			config.headers.Authorization = `Bearer ${newToken}`;

			// Process all queued requests with the new token
			processRefreshSubscribers(newToken);

			return config;
		} catch (err) {
			store.commit('auth/CLEAR_AUTH');
			store.commit('config/CLEAR_CONFIG');
			handleAuthRedirect();
			return Promise.reject(err);
		} finally {
			isRefreshing = false;
			currentRefreshPromise = null;
		}
	},
	error => Promise.reject(error)
);

// Add request/response interceptor
axiosIns.interceptors.response.use(
	response => response, // Pass through successful responses
	async (error) => {
		const { config, response } = error;

		// Skip handling if no response
		if (!response) {
			return Promise.reject(error);
		}

		// Certain URL should be skipped
		if (shouldSkipUrl(config.url)) {
			// Allow policy-related responses even for skipped URLs
			const { data } = response;

			// Skip rejection if it's a policy setup or verification
			if (
				!(data?.data?.requires_policy_setup || data?.data?.requires_policy_verification)
			) {
				// Normal error handling
				return Promise.reject(error);
			}
		}

		// Handle 401 errors (Unauthenticated)
		if (response.status === 401) {
			const originalRequest = config;

			// If we're already refreshing, add to queue
			if (isRefreshing) {
				return await addToRefreshQueue(originalRequest);
			}

			// Check retry count
			originalRequest._retry = originalRequest._retry || 0;
			if (originalRequest._retry >= MAX_RETRIES) {
				store.commit('auth/CLEAR_AUTH');
				store.commit('config/CLEAR_CONFIG');
				handleAuthRedirect();
				return Promise.reject(error);
			}

			// Start refresh process
			isRefreshing = true;
			originalRequest._retry++;

			try {
				// Use a single refresh promise for all concurrent requests
				if (!currentRefreshPromise) {
					currentRefreshPromise = (async () => {
						const token = getLocalStorage('userJwt');
						const refreshToken = getLocalStorage('userRefreshToken');

						if (!token || !refreshToken) {
							throw new Error('No tokens available for refresh');
						}

						const refreshResponse = await axiosNoInterceptors.post(
							api.API_REFRESH_TOKEN,
							{ refresh_token: refreshToken },
							{ headers: { Authorization: `Bearer ${token}` } }
						);

						const {
							access_token: newToken,
							refresh_token: newRefreshToken
						} = refreshResponse.data.data;

						// Update store with new tokens
						store.commit('auth/SET_TOKEN', newToken);
						store.commit('auth/SET_REFRESH_TOKEN', newRefreshToken);

						return newToken;
					})();
				}
				// Wait for the refresh to complete
				const newToken = await currentRefreshPromise;

				// Update the original request with new token
				originalRequest.headers.Authorization = `Bearer ${newToken}`;

				// Process all queued requests with the new token
				processRefreshSubscribers(newToken);

				// Retry the original request with new token
				return axiosIns(originalRequest);
			} catch (refreshError) {
				store.commit('auth/CLEAR_AUTH');
				store.commit('config/CLEAR_CONFIG');
				handleAuthRedirect();
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
				currentRefreshPromise = null;
			}
		}

		// 428 means "requires setup/verification"
		if (response.status === 428 && response.data?.data) {
			const data = response.data.data;

			// If response indicates a policy setup, handle them
			if (data.requires_policy_setup) {
				// Pause original request until 2FA setup completes
				const setupResult = await new Promise(resolve => {
					const event = new CustomEvent('show-2fa-setup', { detail: data });
					const listener = (e) => resolve(e.detail || { success: false });
					window.addEventListener('2fa-setup-completed', listener, { once: true });
					window.dispatchEvent(event);
				});

				// If user cancelled, reject gracefully
				if (!setupResult.success) {
					return Promise.reject({ message: 'Two Factor Setup was Cancelled.' });
				}

				// Retry original request
				return axiosIns(config);
			}

			if (data.requires_policy_verification) {
				const verificationResult = await new Promise(resolve => {
					// 2fa method
					if (data.method === "2fa") {
						const event = new CustomEvent('show-2fa-verification', { detail: data });
						const listener = (e) => resolve(e.detail || { success: false });
						window.addEventListener('2fa-verification-completed', listener, { once: true });
						window.dispatchEvent(event);
					}
					// otp method
					if (data.method === "otp") {
						const event = new CustomEvent('show-otp-verification', { detail: data });
						const listener = (e) => resolve(e.detail || { success: false });
						window.addEventListener('otp-verification-completed', listener, { once: true });
						window.dispatchEvent(event);
					}
				});

				if (!verificationResult.success) {
					return Promise.reject({ message: 'Security Verification was Cancelled.' });
				}

				// Capture verification_id from modal result
				if (verificationResult.security_verification_id) {
					lastSecurityVerificationId = verificationResult.security_verification_id;
				}

				// Attach the security_verification_id to the retried request
				if (lastSecurityVerificationId) {
					if (config.method.toLowerCase() === 'get') {
						// For GET requests, use query params
						config.params = {
							...(config.params || {}),
							security_verification_id: lastSecurityVerificationId,
						};
					} else {
						if (!config.data) {
							// no payload yet, create one
							config.data = { security_verification_id: lastSecurityVerificationId };
						} else {
							try {
								// try to parse JSON string if needed
								const parsedData = typeof config.data === 'string'
									? JSON.parse(config.data)
									: config.data;

								parsedData.security_verification_id = lastSecurityVerificationId;
								config.data = JSON.stringify(parsedData);
							} catch (e) {
								// fallback in case JSON parsing fails
								config.data = JSON.stringify({
									security_verification_id: lastSecurityVerificationId
								});
							}
						}
					}
				}

				// Retry the original request
				return axiosIns(config);
			}
		}

		// Handle 403 errors (Forbidden)
		if (response.status === 403) {
			const originalRequest = config;

			// If already retried after context refresh, redirect to login
			if (originalRequest._authContextRetry) {
				handleAuthRedirect();
				return Promise.reject(error);
			}

			// Mark request to prevent infinite loops
			originalRequest._authContextRetry = true;

			// If we're already refreshing context, add to queue
			if (isFetchingAuthContext) {
				// Wait for ongoing context refresh
				return new Promise(resolve => {
					subscribeToAuthContext(() => {
						resolve(axiosIns(originalRequest));
					});
				});
			}

			// Begin fetching new context
			isFetchingAuthContext = true;

			if (!authContextPromise) {
				const token = getLocalStorage('userJwt');
				if (!token) throw new Error('No token available');

				// Get if there is current selection tenant
				const tenantId = originalRequest.headers['X-Tenant-ID'] || null;
				const currency = originalRequest.headers['X-Currency'] || getLocalStorage('selectedCurrency');

				authContextPromise = axiosNoInterceptors.get(
					api.API_AUTH_CONTEXT,
					{
						headers: {
							Authorization: `Bearer ${token}`,
							'X-Tenant-ID': tenantId,
							'X-Currency': currency
						}
					}
				);
			}

			try {
				const contextResponse = await authContextPromise;
				const { data } = contextResponse.data;

				if (data) {
					// Update store directly with the context data we already have
					store.commit('auth/SET_USER_GROUPS', data.user_groups);
					store.commit('auth/SET_USER_PERMISSIONS', data.permissions);
					store.commit('auth/SET_CURRENT_TENANT', data.current_tenant);
					store.commit('auth/SET_AVAILABLE_TENANTS', data.available_tenants);

					// Update axios header
					axiosIns.defaults.headers.common['X-Tenant-ID'] = data.current_tenant;
				}

				// Retry all pending requests
				processAuthContextSubscribers();

				// Retry original request
				return axiosIns(originalRequest);
			} catch (err) {
				handleAuthRedirect();
				return Promise.reject(err);
			} finally {
				isFetchingAuthContext = false;
				authContextPromise = null;
			}
		}

		return Promise.reject(error);
	}
)

export default axiosIns;
